import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FilePlus, FileSignature, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  listAgreements,
  type AgreementKind,
  type AgreementStatus,
  type AgreementSummary,
} from '@/api/agreements';

/**
 * Lista maestra de agreements (MOU / NDA / Partnership / SOW / DPA / Other).
 * Filtros: tipo + estado + búsqueda por título / contraparte.
 */

const KINDS: AgreementKind[] = ['MOU', 'NDA', 'PARTNERSHIP', 'SOW', 'DPA', 'OTHER'];
const STATUSES: AgreementStatus[] = ['DRAFT', 'IN_REVIEW', 'SIGNED', 'EXPIRED', 'TERMINATED', 'SUPERSEDED'];

const STATUS_COLORS: Record<AgreementStatus, string> = {
  DRAFT:      'bg-muted text-muted-foreground',
  IN_REVIEW:  'bg-amber-900/40 text-amber-200',
  SIGNED:     'bg-emerald-900/40 text-emerald-200',
  EXPIRED:    'bg-orange-900/40 text-orange-200',
  TERMINATED: 'bg-red-900/40 text-red-200',
  SUPERSEDED: 'bg-blue-900/40 text-blue-200',
};

const KIND_COLORS: Record<AgreementKind, string> = {
  MOU:         'bg-indigo-900/40 text-indigo-200',
  NDA:         'bg-purple-900/40 text-purple-200',
  PARTNERSHIP: 'bg-fuchsia-900/40 text-fuchsia-200',
  SOW:         'bg-teal-900/40 text-teal-200',
  DPA:         'bg-cyan-900/40 text-cyan-200',
  OTHER:       'bg-muted text-foreground',
};

export function AgreementsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<AgreementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKind = (searchParams.get('kind') ?? '') as AgreementKind | '';
  const [kindFilter, setKindFilterState] = useState<AgreementKind | ''>(urlKind);
  // Keep filter and URL in sync when the URL changes (sidebar click) or the
  // user picks a different option in the dropdown.
  useEffect(() => { setKindFilterState(urlKind); }, [urlKind]);
  const setKindFilter = (k: AgreementKind | '') => {
    setKindFilterState(k);
    const next = new URLSearchParams(searchParams);
    if (k) next.set('kind', k); else next.delete('kind');
    setSearchParams(next, { replace: true });
  };
  const [statusFilter, setStatusFilter] = useState<AgreementStatus | ''>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAgreements({
      kind: kindFilter || undefined,
      status: statusFilter || undefined,
    })
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kindFilter, statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      a.counterpartyDisplayName.toLowerCase().includes(q) ||
      (a.accountName ?? '').toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileSignature className="h-6 w-6" />
            {t('agreements.list.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('agreements.list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/agreements/import-signed')}>
            <Upload className="mr-1 h-4 w-4" />
            {t('agreements.action.importSigned')}
          </Button>
          <Button size="sm" onClick={() => navigate('/agreements/new')}>
            <FilePlus className="mr-1 h-4 w-4" />
            {t('agreements.action.new')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 rounded-lg border bg-card p-3">
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as AgreementKind | '')}
          className="rounded-md border bg-background px-2 py-1 text-sm"
          aria-label={t('agreements.filter.kind')}
        >
          <option value="">{t('agreements.filter.allKinds')}</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>{t(`agreements.kind.${k}`)}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AgreementStatus | '')}
          className="rounded-md border bg-background px-2 py-1 text-sm"
          aria-label={t('agreements.filter.status')}
        >
          <option value="">{t('agreements.filter.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`agreements.status.${s}`)}</option>
          ))}
        </select>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('agreements.filter.searchPlaceholder')}
          className="flex-1 sm:w-72 sm:flex-initial"
        />
      </div>

      {/* List */}
      {loading && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('common.loading')}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('agreements.list.empty')}
        </div>
      )}
      {!loading && !error && filtered.length > 0 && (
        <ul className="divide-y rounded-lg border bg-card">
          {filtered.map((a) => (
            <li key={a.id}>
              <Link
                to={`/agreements/${a.id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-accent"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${KIND_COLORS[a.kind]}`}>
                      {t(`agreements.kind.${a.kind}`)}
                    </span>
                    <span className="truncate font-medium">{a.title}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {a.counterpartyDisplayName}
                    {a.accountName && a.accountName !== a.counterpartyDisplayName ? ` · ${a.accountName}` : ''}
                    {a.signedDate ? ` · ${t('agreements.column.signed')}: ${a.signedDate}` : ''}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[a.status]}`}>
                  {t(`agreements.status.${a.status}`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
